import { Call, Close } from '@poli-digital/react';
import { Button, Card, CardBody } from '@polichat/flamboyant';
import { useSelector, connect, useDispatch } from 'react-redux';
import { useEffect, useState, useCallback, useMemo, memo } from 'react';
import * as ChatActions from '../../../../../store/modules/chat/actions';
import { didntReply } from 'src/store/modules/chat/actions';
import { bindActionCreators } from 'redux';
import { useTrigger } from 'src/hooks/useTrigger';
import { openVoxPhone } from 'src/lib/openVoxPhone';

// Memoizar o componente para evitar re-renders desnecessários
const CallAfterInactivity = memo(function CallAfterInactivity({
  toContactList,
  opennedChat,
}) {
  const { sendTrigger, triggerTypes } = useTrigger();

  const TIMEOUT_TO_CALL_INACTIVITY = useMemo(
    () => Number(process.env?.REACT_APP_TIMEOUT_TO_CALL_INACTIVITY) ?? 5,
    []
  );

  const [shouldShow, setShouldShow] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const dispatch = useDispatch();

  // Otimizar seletores do Redux para evitar re-renders
  const reduxData = useSelector(
    (state) => ({
      customer_id: state.general?.current_customer_id,
      user_id: state.general?.user.id,
      channels: state.chat?.config?.general?.channels,
    }),
    (left, right) =>
      left.customer_id === right.customer_id &&
      left.user_id === right.user_id &&
      left.channels === right.channels
  );

  const { customer_id, user_id, channels } = reduxData;

  // Memoizar dados do chat para evitar cálculos desnecessários
  const chatData = useMemo(
    () => ({
      didntReplied: opennedChat?.contact?.didnt_reply,
      contact: opennedChat?.contact,
      lastMessage: opennedChat?.last_message,
    }),
    [
      opennedChat?.contact?.didnt_reply,
      opennedChat?.contact,
      opennedChat?.last_message,
    ]
  );

  const { didntReplied, contact, lastMessage } = chatData;

  // Memoizar canal vinculado
  const channelBinded = useMemo(() => {
    if (!channels || !lastMessage?.channel_id) return null;
    return channels.find((channel) => channel?.id === lastMessage.channel_id);
  }, [channels, lastMessage?.channel_id]);

  // Memoizar handlers para evitar re-renders
  const handleCloseOrCalled = useCallback(
    (status) => {
      if (contact?.id) {
        dispatch(didntReply({ contact_id: contact.id, status }));
      }
    },
    [contact?.id, dispatch]
  );

  const handleClickClose = useCallback(() => {
    handleCloseOrCalled(false);
  }, [handleCloseOrCalled]);

  const handleClickVox = useCallback(() => {
    handleCloseOrCalled(true);
    openVoxPhone({
      contactID: contact?.id,
      customerID: customer_id,
      userID: user_id,
      contactName: contact?.name,
      contactPicture: contact?.picture,
    });
  }, [
    contact?.id,
    contact?.name,
    contact?.picture,
    customer_id,
    user_id,
    handleCloseOrCalled,
  ]);

  // Memoizar função checkInactivity
  const checkInactivity = useCallback(() => {
    // Verificar tem dtm, se é whatsapp ou waba se é uma mensagem ativa e se é do tipo message
    if (
      !lastMessage?.dtm ||
      lastMessage?.cuid?.substring(0, 2) !== 's-' ||
      !['whatsapp', 'waba'].includes(channelBinded?.type) ||
      lastMessage?.dir !== 'o' ||
      lastMessage?.event !== 'message'
    ) {
      setShouldShow(false);
      return;
    }

    const lastMessageReceivedDate = new Date(Number(lastMessage.dtm) * 1000);
    const currentDate = new Date();

    // Verificar se é o mesmo dia
    const isSameDay =
      currentDate.getFullYear() === lastMessageReceivedDate.getFullYear() &&
      currentDate.getMonth() === lastMessageReceivedDate.getMonth() &&
      currentDate.getDate() === lastMessageReceivedDate.getDate();

    // Se não for do mesmo dia, não mostrar a sugestão de ligação
    if (!isSameDay) {
      setShouldShow(false);
      return;
    }

    // Verificar a diferença de tempo entre a última mensagem e o tempo atual
    const timeDifference =
      currentDate.getTime() - lastMessageReceivedDate.getTime();
    const minutesDifference = Math.floor(timeDifference / (1000 * 60));

    const newShouldShow = minutesDifference >= TIMEOUT_TO_CALL_INACTIVITY;
    setShouldShow(newShouldShow && didntReplied);
  }, [
    lastMessage?.dtm,
    lastMessage?.cuid,
    lastMessage?.dir,
    lastMessage?.event,
    channelBinded?.type,
    TIMEOUT_TO_CALL_INACTIVITY,
    didntReplied,
  ]);

  // Effect para verificar periodicamente a inatividade
  useEffect(() => {
    checkInactivity();

    // Configura o intervalo para verificar a cada 10 segundos
    const interval = setInterval(checkInactivity, 10000);

    return () => clearInterval(interval);
  }, [checkInactivity, didntReplied]);

  // Effect para controlar a animação
  useEffect(() => {
    if (shouldShow) {
      // Pequeno delay para permitir que o componente seja renderizado antes da animação
      const timer = setTimeout(() => {
        setIsAnimating(true);
      }, 50);

      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
    }
  }, [shouldShow, didntReplied]);

  // Memoizar style object
  const cardStyle = useMemo(
    () =>
      toContactList
        ? {
            position: 'absolute',
            bottom: '0px',
            left: 0,
            width: '100%',
            border: '1px solid var(--border-color)',
            height: 'auto',
            padding: '0px',
          }
        : undefined,
    [toContactList]
  );

  // Memoizar className
  const cardClassName = useMemo(
    () =>
      `wrapper-call-after-inactivity ${
        isAnimating && !toContactList ? 'show' : ''
      }`,
    [isAnimating, toContactList]
  );

  if (!shouldShow) {
    return <></>;
  }

  return (
    <Card className={cardClassName} style={cardStyle}>
      <CardBody className="call-after-inactivity-body">
        <span className="text-sm/[15.4px] font-normal text-start">
          Sem resposta há 5 minutos. Que tal uma ligação?
        </span>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleClickVox();
          }}
          className="call-after-inactivity-button"
        >
          <Call width={16} height={16} />
          <p>Ligar</p>
        </Button>
        {!toContactList && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              if (opennedChat?.id) {
                sendTrigger({
                  entity_id: opennedChat?.id,
                  entity_type: 'contact',
                  type: triggerTypes.CLOSED,
                  origin: 'antiga-interface',
                  payload: {
                    component: 'CallAfterInactivity',
                    hook: null,
                    element: 'close-button-clicked-suggested-call',
                  },
                });
              }
              handleClickClose();
            }}
            size="icon"
            className="x-button-call-after-inactivity"
          >
            <Close width={11} height={11} />
          </Button>
        )}
      </CardBody>
    </Card>
  );
});

// Otimizar mapStateToProps para melhor performance
const mapStateToProps = (state) => {
  const openChat = state.chat?.chats?.find((chat) => chat.open === true);
  return {
    opennedChat: openChat || {},
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(ChatActions, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CallAfterInactivity);
